import React, { useState, useEffect } from "react";
import { useTheme } from "styled-components";
import axios from "axios";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { cn } from "../../../lib/utils";  
import { useEchoMail } from "../../../contexts/EchoMailContext";
import { useAccountSetup } from "../../../contexts/AccountSetupContext";
import {categories} from "../../channel-cats/ChannelCategories";

const Container = ({ children }) => (
  <div className="p-4">{children}</div>
);

const Grid = ({ children }) => (
  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 mb-4">
    {children}
  </div>
);

function Card({ children }) {
  return (
    <div className="flex flex-col items-center justify-end rounded-lg border border-border bg-card pt-3 pb-6 text-center dark:bg-card">
      {children}
    </div>
  );
}

const PlatformIcon = ({ children }) => (
  <div className="m-2 flex items-center justify-center text-4xl">{children}</div>
);

const StatusWrapper = ({ children }) => (
  <div className="flex h-16 w-full flex-col items-center justify-center">
    {children}
  </div>
);

const StatusText = ({ connected, children, comingSoon }) => (
  <p className={cn(
    "m-2", 
    connected ? "text-primary" : "text-muted-foreground"
  )}>
    {comingSoon ? "—" : children}
  </p>
);

const ChannelButton = ({ comingSoon, ...props }) => {
  const theme = useTheme();
  return (
    <Button 
      className={cn(
        "h-10 w-44 px-10 font-semibold rounded-md shadow-lg text-sm",
        comingSoon && "border-0"
      )}
      style={comingSoon ? {
        background: `linear-gradient(to right, ${theme.colors.primaryAccent}, ${theme.colors.primary})`,
        color: theme.colors.buttonTextOnPrimary,
      } : undefined}
      {...props} 
    />
  );
};


export default function ChannelsView() {
  const {
    userId,
    connections: connected,
    loading: isLoading,
    setChannelConnection,
    refreshConnections,
  } = useAccountSetup();

  // EchoMail domain verification states / hooks
  const { domainInfo, reload, getAuthHeaders } = useEchoMail();
  const [trimmedDomainInfo, setTrimmedDomainInfo] = useState(null);
  const [emDropdownOpen, setEmDropdownOpen] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [emLoading, setEmLoading] = useState(false);
  const [emMessage, setEmMessage] = useState(null);

  const apiBase = process.env.REACT_APP_API_URL || "http://localhost:3001";

  useEffect(() => {
    if (!domainInfo || !domainInfo.dkimTokens) return;
    setTrimmedDomainInfo(
      {
        dnsRecords: [
          domainInfo.dkimTokens.map(token => ({
            host: `${token}._domainkey`,
            type: "CNAME",
            data: `${token}.dkim.amazonses.com`,
          })),
        ],
      }
    );
  }, [domainInfo]);

  const authAxios = async () => {
    const headers = await getAuthHeaders();
    return axios.create({ baseURL: apiBase, headers });
  };

  const handleCreateDomain = async () => {
    if (!newDomain) return;
    // Email validation – ensure input follows username@domain.com pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newDomain)) {
      setEmMessage("Please enter a valid email address (e.g., username@domain.com)");
      return;
    }

    setEmLoading(true);
    setEmMessage(null);
    try {
      const client = await authAxios();
      await client.post("/echomail/domain", { email: newDomain });
      setNewDomain("");
      await reload();
    } catch (err) {
      console.error(err);
      setEmMessage(err.response?.data?.error || "Failed to start domain auth");
    } finally {
      setEmLoading(false);
    }
  };

  const handleVerifyDomain = async () => {
    setEmLoading(true);
    setEmMessage(null);
    try {
      const client = await authAxios();
      await client.post("/echomail/domain/verify");
      await reload();
    } catch (err) {
      console.error(err);
      setEmMessage(err.response?.data?.error || "Failed to verify domain");
    } finally {
      setEmLoading(false);
    }
  };

  // Delete existing domain and its SES identity
  const handleDeleteDomain = async () => {
    setEmLoading(true);
    setEmMessage(null);
    try {
      const client = await authAxios();
      await client.delete("/echomail/domain");
      await reload();
    } catch (err) {
      console.error(err);
      setEmMessage(
        err.response?.data?.error || "Failed to delete verified domain"
      );
    } finally {
      setEmLoading(false);
    }
  };

  const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

  // After completing auth flows, callback pages redirect here with `?connected=...`.
  // Use that to update the global connection state immediately (and then clean the URL).
  useEffect(() => {
    const url = new URL(window.location.href);
    const connectedParam = url.searchParams.get("connected");
    if (!connectedParam) return;

    const map = {
      google: "ga",
      ga: "ga",
      gads: "gads",
      googleAds: "gads",
      meta: "meta",
      linkedin: "linkedin",
    };

    const key = map[connectedParam];
    if (key) {
      setChannelConnection(key, true);
      refreshConnections({ silent: true });
    }

    url.searchParams.delete("connected");
    window.history.replaceState({}, "", url.pathname);
  }, [setChannelConnection, refreshConnections]);

  // If this page ever receives OAuth query params directly, keep the global state in sync.
  useEffect(() => {
    if (!userId) return;
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");

    if (!code || !state) return;

    async function clean() {
      url.searchParams.delete("code");
      url.searchParams.delete("state");
      window.history.replaceState({}, "", url.pathname);
    }

    (async () => {
      try {
        if (state === "google") {
          await axios.post(`${API_BASE}/auth/google/exchange-code`, {
            code,
            redirectUri: `${window.location.origin}/auth/google/callback`,
            userId,
          });
          setChannelConnection("ga", true);
        } else if (state === "googleAds") {
          await axios.post(`${API_BASE}/auth/googleAds/exchange-code`, {
            code,
            redirectUri: `${window.location.origin}/auth/google/callback`,
            userId,
          });
          setChannelConnection("gads", true);
        } else if (state === "meta") {
          await axios.post(`${API_BASE}/auth/meta/exchange-code`, {
            code,
            redirectUri: `${window.location.origin}/auth/meta/callback`,
            userId,
          });
          setChannelConnection("meta", true);
        } else if (state === "linkedin") {
          await axios.post(`${API_BASE}/auth/linkedin/exchange-code`, {
            code,
            redirectUri: `${window.location.origin}/auth/linkedin/callback`,
            userId,
          });
          setChannelConnection("linkedin", true);
        }

        // Best-effort re-sync with backend after exchange.
        refreshConnections({ silent: true });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[ChannelsView] exchange error", err);
      } finally {
        await clean();
      }
    })();
  }, [API_BASE, userId, setChannelConnection, refreshConnections]);


  const handleConnect = (key) => {
    if (key === "ga") {
      const cid = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      const red = `${window.location.origin}/auth/google/callback`;
      const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${cid}` +
        `&redirect_uri=${encodeURIComponent(red)}` +
        `&response_type=code&scope=https%3A//www.googleapis.com/auth/analytics.readonly` +
        `&access_type=offline&prompt=consent&state=google`;
      window.location.href = url;

    } else if (key === "gads") {
      const cid = process.env.REACT_APP_GOOGLE_ADS_CLIENT_ID;
      const red = `${window.location.origin}/auth/google/callback`;
      const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${cid}` +
        `&redirect_uri=${encodeURIComponent(red)}` +
        `&response_type=code&scope=https%3A//www.googleapis.com/auth/adwords` +
        `&access_type=offline&prompt=consent&state=googleAds`;
      window.location.href = url;

    } else if (key === "meta") {    
      const appId = process.env.REACT_APP_META_APP_ID;
      const configId =
        process.env.REACT_APP_META_BUSINESS_LOGIN_CONFIG_ID;
      const red = `${window.location.origin}/auth/meta/callback`;
      const scope = encodeURIComponent(
        "public_profile,ads_read,business_management"
      );
      const url =
        `https://www.facebook.com/v22.0/dialog/oauth?client_id=${appId}` +
        `&redirect_uri=${encodeURIComponent(red)}` +
        `&response_type=code` +
        `&config_id=${configId}` +
        `&state=meta&scope=${scope}&auth_type=rerequest`;
      window.location.href = url;

    } else if (key === "linkedin") {
      const cid = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
      const red = `${window.location.origin}/auth/linkedin/callback`;
      const scope = encodeURIComponent("r_ads r_ads_reporting");
      const url = `https://www.linkedin.com/oauth/v2/authorization?response_type=code` +
        `&client_id=${cid}&redirect_uri=${encodeURIComponent(red)}` +
        `&state=linkedin&scope=${scope}`;
      window.location.href = url;

    } else {
      alert(`Connect ${key} clicked`);
    }
  };

  const handleDisconnect = async (key) => {
    try {
      if (!userId) return;
      if (key === "ga") {
        await axios.post(`${API_BASE}/auth/google/disconnect`, { userId });
      } else if (key === "gads") {
        await axios.post(`${API_BASE}/auth/googleAds/disconnect`, { userId });
      } else if (key === "meta") {
        await axios.post(`${API_BASE}/auth/meta/disconnect`, { userId });
      } else if (key === "linkedin") {
        await axios.post(`${API_BASE}/auth/linkedin/disconnect`, { userId });
      }
      setChannelConnection(key, false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[ChannelsView] disconnect error", e);
    }
  };

  return (
    <Container>
      <h1 className="text-2xl font-bold mb-4">Marketing Channels</h1>
       {categories.map((category) => (
      <div key={category.name} className="mb-8">
        <h2 className="text-xl font-semibold mb-4">{category.name}</h2>
        <Grid>
          {category.channels.map((channel) => (
            <Card key={channel.key}>
              <PlatformIcon>{channel.icon}</PlatformIcon>
              <h4>{channel.name}</h4>
              <StatusWrapper>
    {channel.status === "coming-soon" ? (
    <>
      <StatusText connected={false} comingSoon>-</StatusText>
      <ChannelButton comingSoon>Coming Soon!</ChannelButton>
    </>
    ) : (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <StatusText connected={connected[channel.key]}>
            {connected[channel.key] ? "Connected" : "Not Connected"}
          </StatusText>
        )}
        {!isLoading && (
          <>
            {connected[channel.key] ? (
              <ChannelButton
                variant="destructive"
                onClick={() => handleDisconnect(channel.key)}
              >
                Disconnect
              </ChannelButton>
            ) : (
              <ChannelButton onClick={() => handleConnect(channel.key)}
              >
                Connect
              </ChannelButton>
            )}
          </>
        )}
      </>
    )}
  </StatusWrapper>
            </Card>
          ))}
        </Grid>
      </div>
    ))}

      {/* EchoMail Card */}
      <Card>
        <h4>MarkMail</h4>
        <StatusWrapper>
          {/* Determine status based on domainInfo */}
          {emLoading ? (
            <LoadingSpinner />
          ) : (
            (() => {
              const status = domainInfo
                ? domainInfo.verified
                  ? "Verified"
                  : "Pending"
                : "Not Connected";
              return <StatusText connected={domainInfo?.verified}>{status}</StatusText>;
            })()
          )}
          {/* Action buttons */}
          {!emLoading && (
            <div className="flex gap-2">
              {domainInfo ? (
                <>
                  <Button onClick={() => setEmDropdownOpen((o) => !o)}>
                    Manage
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteDomain}
                    disabled={emLoading}
                  >
                    {emLoading ? <LoadingSpinner /> : "Delete"}
                  </Button>
                </>
              ) : (
                <Button onClick={() => setEmDropdownOpen((o) => !o)}>
                  Verify Domain
                </Button>
              )}
            </div>
          )}
        </StatusWrapper>

        {/* Dropdown */}
        {emDropdownOpen && (
          <div className="mt-4 w-full border-t p-4 pb-0 text-left">
            {emMessage && (
              <p className="mb-2 text-sm text-red-500">{emMessage}</p>
            )}

            {domainInfo ? (
              <div className="space-y-2">
                <p>
                  Domain: <strong>{domainInfo.domain}</strong>
                </p>
                {domainInfo.fromEmail && (
                  <p>
                    From Address: <strong>{domainInfo.fromEmail}</strong>
                  </p>
                )}
                <p
                  className={cn(
                    "inline-block rounded px-2 py-1 text-sm font-semibold",
                    domainInfo.verified
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  )}
                >
                  Status: {domainInfo.verified ? "Verified ✔" : "Pending ↺"}
                </p>
                <h4 className="font-semibold">DNS Records</h4>
                <ul className="space-y-1 text-sm">
                  {trimmedDomainInfo?.dnsRecords?.[0]?.map((rec) => (
                    <li
                      key={rec.host}
                      className="border border-gray-200 rounded p-2 space-y-1"
                    >
                      {/* Type row (no copy button) */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium">Type:</span>
                        <code>{rec.type}</code>
                      </div>
                      {/* Host row */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Host:</span>
                          <span>{rec.host}</span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => navigator.clipboard.writeText(rec.host)}
                        >
                          Copy
                        </Button>
                      </div>
                      {/* Data row */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Data:</span>
                          <span>{rec.data}</span>

                        </div>
                        <Button
                          size="sm"
                          onClick={() => navigator.clipboard.writeText(rec.data)}
                        >
                          Copy
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
                {!domainInfo.verified && (
                  <Button className="w-full" onClick={handleVerifyDomain} disabled={emLoading}>
                    {emLoading ? <LoadingSpinner /> : "Verify"}
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex w-full items-center gap-2">
                <Input
                  type="email"
                  className="flex-1"
                  placeholder="username@example.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                />
                <Button onClick={handleCreateDomain} disabled={emLoading}>
                  {emLoading ? <LoadingSpinner /> : "Authenticate"}
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>


    </Container>
  );
}